import "../assets/send.scss";
import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Avatar, Box, Drawer } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import QrScanner from "qr-scanner";
import { FileUploader } from "react-drag-drop-files";
import Dialog from "./dialog/Dialog";
import {
  imageInvalid,
  imageSizeInvalid,
  imageSizeMsg,
  notQrCode,
  proceed,
  sats,
  send,
  sendAsset,
  sendMessage,
  uploadImageText,
  sendMessageLabel,
  recentSentLabel,
  memoRequired,
  memoRequiredMsg,
  confirmMsg,
  XAUT,
} from "./messages";
import { Input } from "./input/Input";
import {
  allowedImageTypes,
  callAPIInterface,
  receiveViaMethod,
  sendViaMethod,
  withdrawRequest,
  addressAutoCompleteOptions,
  paymentMethodTags,
} from "./constants";
import Text from "./text/Text";
import Button from "./button/Button";
import { bitCoinSymbol } from "./images";
import SendViaSection from "./SendViaSection";
import { setSendToContactData, showToast } from "../redux/actions";
import PaymentModeModal from "./PaymentModeModal";
import WalletSymbols from "./WalletSymbols";
import InvalidErrorModal from "./InvalidErrorModal";
import { Tag } from "./tag/Tag";
import RecentSentSkeleton from "./RecentSentSkeleton";
import { handleDecode, handleProceed } from "./sendFlowConstants";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

function SendCommon({
  isSendModalOpen,
  setIsSendModalOpen,
  directionToOpen,
  customClass,
  isFromBrowser,
  ...props
}) {
  const dispatch = useDispatch();
  const { taprootAsset, sendToContactData, payViaMethods } = useSelector(
    (state) => state.common
  );

  const [address, setAddress] = useState(props?.addressFromWindow || "");
  const [imageError, setImageError] = useState(false);
  const [showSendVia, setShowSendVia] = useState(false);
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(data?.amount);
  const [btnLoader, setBtnLoader] = useState(false);
  const [isOpenPaymentModeModal, setIsOpenPaymentModeModal] = useState(false);
  const [selectedMode, setSelectedMode] = useState("");
  const [secondBitcoinAddress, setSecondBitcoinAddress] = useState("");
  const [secondLightningAddress, setLightningAddress] = useState("");
  const [isShowErrorModal, setIsShowErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorMsgHeader, setErrorMsgHeader] = useState("");
  const [url, setUrl] = useState(window.location.href);
  const [isSendMax, setIsSendMax] = useState(false);
  const [noteFromURL, setNoteFromURL] = useState(null);
  const [currencyFromDecode, setCurrencyFromDecode] = useState(null);
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState(1);
  const [domainOptions, setDomainOptions] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [recentSents, setRecentSents] = useState([]);
  const [isRecentLoading, setIsRecentLoading] = useState(true);
  const [isMemoDrawerOpen, setIsMemoDrawerOpen] = useState(false);

  const { uuid: currency, icon: currencyIcon } = { ...taprootAsset };

  const handleDecodeCommonStates = {
    setIsOpenPaymentModeModal,
    setBtnLoader,
    currency,
    setCurrencyExchangeRate,
    setCurrencyFromDecode,
    setAmount,
    setData,
    setNoteFromURL,
    setShowSendVia,
    setIsShowErrorModal,
    setErrorMsg,
    setErrorMsgHeader,
    address,
    payViaMethods,
    setIsMemoDrawerOpen,
  };

  const handleProcessCommonStates = {
    setNoteFromURL,
    setLightningAddress,
    setSecondBitcoinAddress,
    setAmount,
    setIsOpenPaymentModeModal,
    ...handleDecodeCommonStates,
  };

  const addressOptions = useMemo(() => {
    return contactOptions.length ? contactOptions : domainOptions;
  }, [domainOptions, contactOptions]);

  const clearSearch = () => {
    props?.setAddressFromWindow(null);
    const newUrl = new URL(url);
    newUrl.search = "";
    setUrl(newUrl.href);
    window.history.replaceState(null, "", newUrl.href);
  };

  useEffect(() => {
    if (imageError) {
      dispatch(
        showToast({
          isToastOpen: true,
          toastMessage: imageError,
          toastVariant: "error",
        })
      );
      setImageError(false);
    }
  }, [imageError]);

  useEffect(() => {
    if (selectedMode !== "") {
      const selectedAddress =
        selectedMode === "bitcoin"
          ? secondBitcoinAddress || ""
          : secondLightningAddress || "";

      handleDecode({
        addr: selectedAddress,
        amountFromURL: amount,
        ...handleDecodeCommonStates,
      });
    }
  }, [selectedMode]);

  const getContactsOptions = () => {
    const apiData = { query: `is:customer name~${address}` };
    callAPIInterface("POST", "/search", apiData)
      .then((res) => {
        const result = res?.data
          ?.flatMap(
            (item) =>
              item?.payment_address_details?.map((address, index) => ({
                key: `${address?.id}-${index}`,
                label: item?.name,
                object: "customer",
                payment_address: address?.payment_address,
                payment_address_id: address?.id,
                payment_method: address?.payment_method,
              })) || []
          )
          .filter(Boolean);

        setContactOptions(structuredClone(result) || []);
      })
      .catch((err) => {
        console.error("Error fetching contacts options:", err);
        setContactOptions([]);
      });
  };

  useEffect(() => {
    getRecentSentData();
    if (props?.addressFromWindow) {
      handleProceed({
        invoiceUrl: props?.addressFromWindow,
        ...handleProcessCommonStates,
      });
    }
  }, []);

  useEffect(() => {
    if (address && address.length >= 3 && !domainOptions.length) {
      const debouncedSearch = setTimeout(() => {
        getContactsOptions();
      }, 500);

      return () => clearTimeout(debouncedSearch);
    }

    setContactOptions([]);
  }, [address]);

  useEffect(() => {
    const { payment_address_id, payment_address } = sendToContactData;
    if (payment_address_id && payment_address) {
      setSelectedContact(sendToContactData);
      setAddress(payment_address);
      handleProceed({
        invoiceUrl: payment_address,
        ...handleProcessCommonStates,
      });
      dispatch(setSendToContactData({}));
    }
  }, [sendToContactData]);

  const onUpload = async (e) => {
    await QrScanner.scanImage(e, {
      returnDetailedScanResult: true,
      scanRegion: {
        downScaledHeight: 500,
        downScaledWidth: 500,
      },
      disallowCanvasResizing: false,
    })
      .then((res) => {
        setImageError("");
        handleProceed({ invoiceUrl: res?.data, ...handleProcessCommonStates });
      })
      .catch((_err) => {
        dispatch(
          showToast({
            isToastOpen: true,
            toastMessage: notQrCode,
            toastVariant: "error",
          })
        );
      });
  };

  const getRecentSentData = async () => {
    setIsRecentLoading(true);
    callAPIInterface("GET", `/send/recent-contacts`)
      .then((res) => {
        const result = res?.data
          ?.flatMap(
            (item) =>
              item?.payment_address_details?.map((address) => ({
                key: address.id,
                label: item.name,
                object: "customer",
                payment_address: address.payment_address,
                payment_address_id: address.id,
                payment_method: address.payment_method,
              })) || []
          )
          .filter(Boolean);

        setRecentSents(structuredClone(result) || []);
      })
      .catch((err) => {
        console.error("Error fetching contacts options:", err);
        setRecentSents([]);
      })
      .finally(() => setIsRecentLoading(false));
  };

  const renderCustomerContent = (item) => {
    const name = item?.label?.split(" ");
    let initials = name?.[0]?.charAt(0) + (name?.[1]?.charAt(0) || "");
    initials = initials?.toUpperCase() ?? "#";

    return (
      <>
        <Avatar
          sx={{
            backgroundColor: "#F7FAFC",
            border: "1px solid #F6F3F3",
            marginRight: "8px",
          }}
        >
          <Text sx={{ color: "#0A193E" }}>{initials}</Text>
        </Avatar>

        <Box display="flex" flexDirection="column">
          <Text className="default-text" size={16} font="medium">
            {item?.label}
          </Text>
          <Text size={14} font="regular" className="grey-text">
            {item?.payment_address}
          </Text>
          <Tag
            text={paymentMethodTags[item?.payment_method]?.title}
            font="medium"
            className="payment-method-tag"
            style={{
              background: paymentMethodTags[item?.payment_method]?.background,
            }}
          />
        </Box>
      </>
    );
  };

  const sentItem = (recentSent) => {
    return (
      <Box
        className="recent-contact-list-item"
        key={recentSent.key}
        sx={{
          backgroundColor: "#fff",
          border: "0",
          padding: "10px 0px",
        }}
        onClick={() => {
          setAddress(recentSent.payment_address);
          if (recentSent.object === "customer") {
            setSelectedContact(recentSent);
            handleProceed({
              invoiceUrl: recentSent.payment_address,
              ...handleProcessCommonStates,
            });
          }
        }}
      >
        {renderCustomerContent(recentSent)}
      </Box>
    );
  };

  const displaySendViaHeader = () => {
    if (data) {
      return data?.tag === withdrawRequest
        ? receiveViaMethod(data?.withdraw_method, data?.withdraw_request)
        : sendViaMethod(data?.withdraw_method, data?.withdraw_request);
    } else {
      return send;
    }
  };

  const fileUploadJsx = () => {
    return (
      <Box className="image-upload-box" id="image-upload-box">
        <WalletSymbols
          sx={{
            height: "24px",
            width: "24px",
            color: "#848B9E",
            marginBottom: "14px",
          }}
          symbol="upload_file"
        />

        <Text className="grey-text" size={14} font="medium">
          {uploadImageText[0]}
          <span style={{ color: "#2A67FF" }}>{uploadImageText[1]}</span>
        </Text>
        <Text className="grey-text" size={14} font="regular" sx={{ mt: "4px" }}>
          {imageSizeMsg}
        </Text>
      </Box>
    );
  };

  const handleModalClose = () => {
    setIsShowErrorModal(false);
    setIsSendModalOpen(false);
  };

  const renderAddressOption = (props, option) => {
    if (option?.object === "customer") {
      return (
        <Box
          {...props}
          key={option.key}
          display="flex"
          alignItems="start"
          padding="10px"
        >
          {renderCustomerContent(option)}
        </Box>
      );
    } else {
      const addressOption = option?.payment_address?.split(address);
      return (
        <Text
          {...props}
          sx={{
            borderBottom: "1px solid #0B254B1F",
          }}
        >
          <span style={{ color: "#848B9E" }}>{address}</span>
          {addressOption?.[1]}
        </Text>
      );
    }
  };

  const handleAddressChange = (newInputValue) => {
    if (!newInputValue.includes("@")) setDomainOptions([]);
    if (newInputValue.split("@").length == 2) {
      const name = newInputValue.split("@")[0];
      const options = addressAutoCompleteOptions.map((option) => {
        return {
          payment_address: name.concat(`@${option}`),
          label: name.concat(`@${option}`),
          object: "domain",
        };
      });
      setDomainOptions(options);
    }
    setAddress(newInputValue);
  };

  return (
    <Dialog
      isOpenModal={isSendModalOpen}
      setIsOpenModal={setIsSendModalOpen}
      modalHeader={displaySendViaHeader()}
      directionToOpen={directionToOpen}
      handleAction={() => {
        window.location.search && clearSearch();
        setIsSendModalOpen(false);
        setShowSendVia(false);
        setAddress(null);
      }}
      isShowCloseIcon={isFromBrowser}
    >
      <Box className={customClass}>
        <Box className="send-container">
          {!showSendVia ? (
            <>
              <Box width="100%" padding={"8px 16px"} boxSizing="border-box">
                {isFromBrowser && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <img
                      src={currency === sats ? bitCoinSymbol : currencyIcon}
                      height="54px"
                      width="54px"
                      style={{ marginBottom: "5px" }}
                      alt="currency icon"
                    />
                    <Text className="default-text" size={20} font="semibold">
                      {sendAsset(currency === sats ? "BTC" : currency)}
                    </Text>
                  </Box>
                )}
                <Autocomplete
                  freeSolo
                  value={address}
                  onChange={(_, newAddress) => {
                    setAddress(newAddress?.payment_address);
                    if (newAddress?.object === "customer") {
                      setSelectedContact(newAddress);
                      handleProceed({
                        invoiceUrl: newAddress?.payment_address,
                        ...handleProcessCommonStates,
                      });
                    }
                  }}
                  options={addressOptions}
                  renderOption={renderAddressOption}
                  filterOptions={(options) =>
                    options.filter((option) => {
                      if (option?.object === "domain") {
                        return option?.label
                          ?.toLowerCase()
                          .includes(address.toLowerCase());
                      }
                      return true;
                    })
                  }
                  disableClearable
                  renderInput={(params) => (
                    <Input
                      {...params.InputProps}
                      inputProps={{
                        ...params.inputProps,
                      }}
                      value={address}
                      onChange={(e) => handleAddressChange(e.target.value)}
                      fullWidth
                      showLabel
                      label={!isFromBrowser ? sendMessageLabel : ""}
                      placeholder={sendMessage}
                      name="lnAddress"
                      type="text"
                      autoFocus={true}
                      className="address-input-field"
                    />
                  )}
                />

                <Text
                  className="grey-text"
                  size={isFromBrowser ? 16 : 14}
                  font="regular"
                  sx={{ margin: "12px 0", textAlign: "center" }}
                >
                  {"or"}
                </Text>

                <FileUploader
                  name="file"
                  types={allowedImageTypes}
                  maxSize={2000000}
                  onSizeError={() => setImageError(imageSizeInvalid)}
                  onTypeError={() => setImageError(imageInvalid)}
                  dropMessageStyle={{ backgroundColor: "#E4E9EE" }}
                  children={fileUploadJsx()}
                  onDrop={(e) => onUpload(e)}
                  onSelect={(e) => onUpload(e)}
                  hoverTitle=""
                  required={false}
                />

                {imageError && (
                  <Text className="error-text" size={14} font="regular">
                    {imageError}
                  </Text>
                )}

                {(isRecentLoading || recentSents.length > 0) && (
                  <Box className="recent-list-wrapper">
                    <Box className="recent-contact-list">
                      {isRecentLoading ? (
                        <RecentSentSkeleton />
                      ) : (
                        <>
                          {recentSents.length > 0 && (
                            <>
                              <Text
                                className="default-text mb-8"
                                size={16}
                                font="semibold"
                              >
                                {recentSentLabel}
                              </Text>
                              {recentSents.map((recent) => sentItem(recent))}
                            </>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
              <Box className="options-footer-btn ln-address-footer">
                <Button
                  fullWidth
                  variant="contained"
                  className="proceed txt-uppercase"
                  label={proceed}
                  onClick={() =>
                    handleProceed({
                      invoiceUrl: address,
                      ...handleProcessCommonStates,
                    })
                  }
                  disabled={!address || btnLoader}
                  loading={btnLoader}
                />
              </Box>
            </>
          ) : (
            <SendViaSection
              isFromBrowser={isFromBrowser}
              data={data}
              amount={amount}
              setAmount={setAmount}
              setIsSendModalOpen={setIsSendModalOpen}
              setAddressFromWindow={props?.setAddressFromWindow}
              isSendMax={isSendMax}
              setIsSendMax={setIsSendMax}
              noteFromURL={noteFromURL}
              currency={currencyFromDecode}
              setCurrency={setCurrencyFromDecode}
              currencyExchangeRate={currencyExchangeRate}
              selectedContact={selectedContact}
            />
          )}
        </Box>
      </Box>
      {isOpenPaymentModeModal && (
        <PaymentModeModal
          handleModalClose={() => setIsOpenPaymentModeModal(false)}
          openModal={isOpenPaymentModeModal}
          setSelectedMode={setSelectedMode}
        />
      )}
      {isShowErrorModal && (
        <InvalidErrorModal
          isFromBrowser={isFromBrowser}
          isOpenInvalidQRModal={isShowErrorModal}
          handleModalClose={handleModalClose}
          setIsOpenInvalidQRModal={setIsShowErrorModal}
          icon="invalid"
          message={errorMsgHeader}
          subMessage={errorMsg}
          direction={"up"}
          customClass={isFromBrowser ? "error-modal-wrapper-options" : ""}
          handleButtonAction={handleModalClose}
        />
      )}
      <Drawer
        anchor="bottom"
        className="memo-drawer"
        open={isMemoDrawerOpen}
        onClose={() => setIsMemoDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: "14px",
            borderTopRightRadius: "14px",
          },
        }}
      >
        <Box padding="24px 16px">
          <Box display="flex" justifyContent="end">
            <CloseIcon
              sx={{
                color: "#848B9E",
                height: "20px",
                width: "20px",
                marginRight: "8px",
                cursor: "pointer",
              }}
              onClick={() => setIsMemoDrawerOpen(false)}
            />
          </Box>
          <Box className="drawer-header">
            <Box display="flex" justifyContent="center" mb={2}>
              <ErrorOutlineIcon
                sx={{ color: "#D88A2C", height: "54px", width: "54px" }}
              />
            </Box>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Text
              className="default-text"
              size={20}
              font="bold"
              mb="20px"
              sx={{ textAlign: "center" }}
            >
              {memoRequired}
            </Text>
            <Text
              className="default-text"
              size={14}
              font="regular"
              component="span"
              sx={{ textAlign: "center" }}
            >
              {memoRequiredMsg}
            </Text>
          </Box>
          <Button
            variant="outlined"
            className="submit-btn"
            label={confirmMsg}
            onClick={() => setIsMemoDrawerOpen(false)}
            sx={{ width: "100%", marginTop: "20px" }}
          />
        </Box>
      </Drawer>
    </Dialog>
  );
}

export default SendCommon;
